import { useState, useEffect, useRef, HTMLAttributes, HtmlHTMLAttributes, SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import styles from './index.module.scss';
import { twMerge } from 'tailwind-merge';
import { WeeeIcon } from '../Icon';
import { fixedBody, looseBody } from '@/utils/scroll';

export type PopupProps = {
  show: boolean;
  onClose?: (e?: SyntheticEvent) => void;
  afterClose?: () => void;
  animationType?: 'slide-up' | 'slide-down' | 'scale-in' | 'scale-out' | 'slide-left' | 'slide-right' | 'none';
  zIndex?: number;
  destroyOnClose?: boolean;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  title?: string | React.ReactNode;
  children: React.ReactNode;
  animateTime?: number;
  closable?: boolean;
  popupId?: string;
  /* 内容区域的类名 */
  wrapperClassName?: HtmlHTMLAttributes<HTMLElement>['className'];
  className?: HtmlHTMLAttributes<HTMLElement>['className'];
  wrapperStyle?: React.CSSProperties;
  popStyle?: React.CSSProperties;
  maskClosable?: boolean;
  maskClassName?: string;
  headerClassName?: string;
  popupProps?: any;
  isFixedBody?: boolean;
  closeIconClassName?: string;
  titleClassName?: string;
  contentDataTestId?: string;
  titleDataTestId?: string;
};

const Popup = ({
  show,
  onClose = () => {},
  afterClose = () => {},
  animationType = 'scale-in',
  zIndex = 1000,
  destroyOnClose = false,
  position = 'center',
  title = '',
  children,
  animateTime = 250,
  closable = false,
  popupId = 'popup',
  className = '',
  wrapperClassName = '',
  wrapperStyle = {},
  headerClassName = '',
  maskClassName = '',
  popStyle = {},
  maskClosable = true,
  isFixedBody = true,
  closeIconClassName = '',
  titleClassName = '',
  titleDataTestId = '',
  contentDataTestId
}: PopupProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(show);
  const [firstRendered, setFirstRendered] = useState<boolean>(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const handleClose = (e: SyntheticEvent) => {
    onClose(e);
  };

  useEffect(() => {
    // 如果有其他的popup打开，那么不需要执行looseBody
    const otherOpenPopup = document.querySelector(`[data-popup-visible="true"]:not(#${popupId})`);
    if (show) {
      if (popupRef.current) {
        popupRef.current.focus();
      }
      !otherOpenPopup && isFixedBody && fixedBody();
      setFirstRendered(true);
      setIsOpen(true);
    } else {
      if (!otherOpenPopup) {
        isFixedBody && looseBody();
      }
      setIsOpen(false);
    }
    return () => {
      if (otherOpenPopup) return;
      isFixedBody && looseBody();
    };
  }, [show]);

  useEffect(() => {
    if (!show && destroyOnClose && popupRef.current) {
      const timer = setTimeout(() => {
        afterClose();
        firstRendered && setFirstRendered(false);
        clearTimeout(timer);
        popupRef.current?.remove?.();
      }, 300);
    }
  }, [show, destroyOnClose, afterClose]);
  if (!firstRendered && !show) {
    return null;
  }
  if (!isOpen && !firstRendered) return null; // 首次show组件时，默认状态是隐藏的，所以不渲染，防止闪现

  return createPortal(
    <div
      aria-modal={true}
      role="dialog"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      data-type="popup"
      data-popup-visible={isOpen}
      id={popupId}
      style={{ zIndex, animationDuration: `${animateTime}ms`, transform: 'translateZ(5px)', ...popStyle }}
      ref={popupRef}
      data-testid={`wid-popup-${popupId}`}
      className={twMerge(
        `fixed inset-0 flex items-center justify-center ${!isOpen ? styles['zHide'] : ''}`,
        className
      )}>
      <div
        className={twMerge(
          `touch-none absolute inset-0 bg-reserved-true-black opacity-30 transition-opacity duration-300 ease-out ${
            isOpen ? styles['fade-in'] : styles['fade-out']
          }`,
          maskClassName
        )}
        onClick={e => {
          e.stopPropagation();
          if (maskClosable) {
            handleClose(e);
          }
        }}
        data-testid={`wid-popup-mask`}
      />
      <div
        data-testid={contentDataTestId ?? `wid-popup-${popupId}-content`}
        style={{ animationDuration: `${animateTime}ms`, ...wrapperStyle }}
        className={twMerge(
          `${popupId}-content`,
          `enki-elevation-5 border border-tint-black-600 touch-none bg-surface-100-bg max-w-[750px] mx-auto box-border inset-x-0 transform transition duration-300 ${
            isOpen ? styles[animationType + '-enter'] : styles[animationType + '-leave']
          } absolute`,
          position === 'top' && 'top-0',
          position === 'bottom' && 'bottom-0 inset-x-0 pb-safe',
          position === 'left' && 'left-0 inset-y-0',
          position === 'right' && 'right-0',
          position === 'center' && 'relative',
          wrapperClassName
        )}>
        {(title || closable) && (
          <header
            id={`${popupId}-header`}
            data-testid={`wid-popup-header`}
            className={twMerge('p-5 relative flex justify-center items-center', headerClassName)}>
            {title && typeof title === 'string' ? (
              <h2 data-testid="wid-popup-header-title" className={twMerge('enki-body-base-medium', titleClassName)}>
                {title}
              </h2>
            ) : (
              title
            )}
            {closable && (
              <div
                className={twMerge('absolute left-5 top-1/2 -translate-y-1/2', closeIconClassName)}
                data-testid="btn-modal-close"
                onClick={handleClose}>
                <WeeeIcon type="iconclose" className="text-surface-100-fg" style={{ fontSize: 19 }} />
              </div>
            )}
          </header>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

export default Popup;
